// EditVehicleForm.jsx
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Tab,
  Nav,
  Card,
  Alert,
  ProgressBar,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { Baseurl } from "../../baseurl";
import moment from "moment";

const EditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("basic");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingVahan, setIsFetchingVahan] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    // 🆔 Vehicle Identity & Basic Info
    title: "",
    brand: "",
    model: "",
    variant: "",
    color: "",

    // Manufacturing details
    manufacturing: {
      monthYear: "",
      year: new Date().getFullYear(),
    },

    // Registration details
    registration: {
      number: "",
      authority: "",
      date: null,
      expiryDate: null,
      normsType: "",
      status: "",
      statusAsOn: "",
      rtoCode: "",
      financer: "",
      isCommercial: false,
    },

    // Identifiers
    identifiers: {
      chassis: "",
      engine: "",
      class: "",
      bodyType: "",
      vehicleNumber: "",
    },

    // Owner details
    owner: {
      name: "",
      fatherName: "",
      mobile: "",
      count: "First",
    },

    // Addresses
    addresses: {
      present: {
        full: "",
        district: "",
        city: "",
        state: "",
        pincode: "",
      },
      permanent: {
        full: "",
        district: "",
        city: "",
        state: "",
        pincode: "",
      },
    },

    // Technical specifications
    specifications: {
      cubicCapacity: "",
      cylinders: "",
      seatCapacity: "",
      sleeperCapacity: "",
      standingCapacity: "",
      grossWeight: "",
      unladenWeight: "",
      wheelbase: "",
      category: "Car",
    },

    // EV Specifications
    evSpecs: {
      batteryCapacity: {
        value: "",
        unit: "kWh",
      },
      batteryType: "Li-ion",
      batteryHealthPercent: 100,
      chargerStatus: false,
      range: {
        value: "",
        unit: "km",
      },
      charging: {
        chargingType: "AC",
        normalTime: "",
        fastTime: "",
      },
      motorPower: {
        value: "",
        unit: "kW",
      },
      maxSpeed: "",
    },

    // Insurance
    insurance: {
      company: "",
      policyNumber: "",
      validUpto: null,
    },

    // Permits
    permits: {
      permitNumber: "",
      permitType: "",
      validFrom: null,
      validUpto: null,
      nationalPermitNumber: "",
      nationalPermitUpto: null,
      issuedBy: "",
    },

    // PUC
    puc: {
      number: "",
      validUpto: null,
    },

    // Location/Yard
    yard: {
      parkedAt: "",
      address: "",
      pocName: "",
      pocContact: "",
    },

    // Media
    media: {
      images: [],
      videos: [],
    },

    // Documents
    documents: {
      rcBook: "",
      insuranceCopy: "",
      fitnessCertificate: "",
      taxReceipt: "",
      invoice: "",
      batteryWarranty: {
        validTill: null,
        remainingMonths: "",
      },
      vehicleWarranty: {
        validTill: null,
        remainingMonths: "",
      },
      fastag: {
        fastagNo: "",
        balance: "",
      },
    },

    // Pricing and Auction
    pricing: {
      expectedPrice: "",
      basePrice: "",
      reservePrice: "",
      currentBid: 0,
    },

    auction: {
      startTime: new Date(),
      endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
      status: "Draft",
      bids: [],
      winner: null,
    },

    // Vahan details
    vahanDetails: null,

    // Valuation Report
    valuationReport: "",

    // System flags
    flags: {
      isActive: true,
      isFeatured: false,
      isVerified: true,
    },
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [uploadedImagePaths, setUploadedImagePaths] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uploadedVideoPaths, setUploadedVideoPaths] = useState([]);
  const [documentFiles, setDocumentFiles] = useState({});

  // Fetch vehicle data on component mount
  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  const fetchVehicleData = async () => {
    if (!id) {
      toast.error("Vehicle ID not found");
      navigate("/auctions/list");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${Baseurl}/api/vehicles/${id}`);

      if (response.data.success) {
        const vehicleData = response.data.data;
        setOriginalData(vehicleData);

        // Parse date strings into Date objects
        const parsedData = parseDatesInVehicleData(vehicleData);
        
        // Separate image paths from the data
        if (parsedData.media?.images) {
          setUploadedImagePaths(parsedData.media.images);
          // Clear image files as we're showing uploaded paths
          setImageFiles([]);
        }
        
        if (parsedData.media?.videos) {
          setUploadedVideoPaths(parsedData.media.videos);
          setVideoFiles([]);
        }

        setFormData(parsedData);
        toast.success("Vehicle data loaded successfully");
      } else {
        toast.error("Failed to load vehicle data");
        navigate("/auctions/list");
      }
    } catch (error) {
      console.error("Error fetching vehicle:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch vehicle data"
      );
      navigate("/auctions/list");
    } finally {
      setIsLoading(false);
    }
  };

  const parseDatesInVehicleData = (data) => {
    const parsedData = { ...data };

    // Parse nested date strings
    const parseDate = (dateString) => {
      if (!dateString) return null;
      return new Date(dateString);
    };

    // Parse registration dates
    if (data.registration) {
      parsedData.registration.date = parseDate(data.registration.date);
      parsedData.registration.expiryDate = parseDate(data.registration.expiryDate);
    }

    // Parse insurance date
    if (data.insurance) {
      parsedData.insurance.validUpto = parseDate(data.insurance.validUpto);
    }

    // Parse permit dates
    if (data.permits) {
      parsedData.permits.validFrom = parseDate(data.permits.validFrom);
      parsedData.permits.validUpto = parseDate(data.permits.validUpto);
      parsedData.permits.nationalPermitUpto = parseDate(data.permits.nationalPermitUpto);
    }

    // Parse PUC date
    if (data.puc) {
      parsedData.puc.validUpto = parseDate(data.puc.validUpto);
    }

    // Parse warranty dates
    if (data.documents) {
      if (data.documents.batteryWarranty) {
        parsedData.documents.batteryWarranty.validTill = parseDate(data.documents.batteryWarranty.validTill);
      }
      if (data.documents.vehicleWarranty) {
        parsedData.documents.vehicleWarranty.validTill = parseDate(data.documents.vehicleWarranty.validTill);
      }
    }

    // Parse auction dates
    if (data.auction) {
      parsedData.auction.startTime = parseDate(data.auction.startTime);
      parsedData.auction.endTime = parseDate(data.auction.endTime);
    }

    return parsedData;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        let current = updated;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = type === "checkbox" ? checked : value;
        return updated;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Handle nested object changes
  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  // Handle deeply nested changes
  const handleDeepNestedChange = (parent1, parent2, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent1]: {
        ...prev[parent1],
        [parent2]: {
          ...prev[parent1][parent2],
          [field]: value,
        },
      },
    }));
  };

  // Fetch Vahan details
  const fetchVahanDetails = async () => {
    const regNumber = formData.registration.number;

    if (!regNumber) {
      toast.error("Please enter registration number first");
      return;
    }
    const uploadData = {
      regdNo: regNumber,
    };

    setIsFetchingVahan(true);
    try {
      const config = {
        headers: { "Content-Type": "application/json" },
        Authorization: "t0itN3Np4tAJZjd7wPKlHvMpWWKj1fiU",
      };
      const url = `https://instavaluermainapi.instavaluer.com/api/v1/cases/sendvehiclenumber`;
      const resp = await axios.post(url, uploadData, config);
      
      if (resp.data.success) {
        const vahanData = resp.data.response?.result;
        setFormData((prev) => ({
          ...prev,
          vahanDetails: vahanData,
        }));
        toast.success("Vahan details fetched successfully!");
      }
    } catch (error) {
      console.error("Vahan fetch error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch Vahan details"
      );
    } finally {
      setIsFetchingVahan(false);
    }
  };

  // Upload files to server
  const uploadFilesToServer = async (files, type) => {
    try {
      const formDataToSend = new FormData();
      files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        maxBodyLength: Infinity,
      };

      const res = await axios.post(
        `${Baseurl}/auctionVehicleUpload`,
        formDataToSend,
        config,
      );

      if (res.data && res.data.success) {
        return {
          success: true,
          filePaths: res.data.filePaths,
        };
      }
      return { success: false, error: "Upload failed" };
    } catch (error) {
      console.error(`${type} upload error:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Handle file uploads for images and videos
  const handleMultipleFileUpload = async (e, field, isMedia = false) => {
    try {
      const files = Array.from(e.target.files);

      if (isMedia) {
        if (field === "images") {
          // Store File objects for preview
          setImageFiles((prev) => [...prev, ...files]);

          // Upload to server
          const uploadResult = await uploadFilesToServer(files, "images");
          if (uploadResult.success) {
            const filePaths = uploadResult.filePaths;
            setUploadedImagePaths((prev) => [...prev, ...filePaths]);

            // Update formData with image paths
            setFormData((prev) => ({
              ...prev,
              media: {
                ...prev.media,
                images: [...prev.media.images, ...filePaths],
              },
            }));

            toast.success(`${files.length} image(s) uploaded successfully!`);
          } else {
            // Remove from preview if upload failed
            setImageFiles((prev) => prev.slice(0, prev.length - files.length));
            toast.error("Failed to upload image(s)");
          }
        } else if (field === "videos") {
          // Store File objects for preview
          setVideoFiles((prev) => [...prev, ...files]);

          // Upload to server
          const uploadResult = await uploadFilesToServer(files, "videos");
          if (uploadResult.success) {
            const filePaths = uploadResult.filePaths;
            setUploadedVideoPaths((prev) => [...prev, ...filePaths]);

            // Update formData with video paths
            setFormData((prev) => ({
              ...prev,
              media: {
                ...prev.media,
                videos: [...prev.media.videos, ...filePaths],
              },
            }));

            toast.success(`${files.length} video(s) uploaded successfully!`);
          } else {
            // Remove from preview if upload failed
            setVideoFiles((prev) => prev.slice(0, prev.length - files.length));
            toast.error("Failed to upload video(s)");
          }
        }
      } else {
        // For documents - single file per field
        const fieldName = field.split(".")[1];
        const file = files[0];

        const uploadResult = await uploadFilesToServer([file], "document");
        if (uploadResult.success) {
          const filePath = uploadResult.filePaths[0];

          setDocumentFiles((prev) => ({
            ...prev,
            [fieldName]: file,
          }));

          setFormData((prev) => ({
            ...prev,
            documents: {
              ...prev.documents,
              [fieldName]: filePath,
            },
          }));

          toast.success(`Document uploaded successfully!`);
        } else {
          toast.error("Failed to upload document");
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file(s)");
    }
  };

  // Remove image
  const handleRemoveImage = (index) => {
    // Remove from preview files
    setImageFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });

    // Remove from uploaded paths
    setUploadedImagePaths((prev) => {
      const newPaths = [...prev];
      newPaths.splice(index, 1);
      return newPaths;
    });

    // Remove from formData
    setFormData((prev) => ({
      ...prev,
      media: {
        ...prev.media,
        images: prev.media.images.filter((_, i) => i !== index),
      },
    }));

    toast.info("Image removed");
  };

  // Remove video
  const handleRemoveVideo = (index) => {
    // Remove from preview files
    setVideoFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });

    // Remove from uploaded paths
    setUploadedVideoPaths((prev) => {
      const newPaths = [...prev];
      newPaths.splice(index, 1);
      return newPaths;
    });

    // Remove from formData
    setFormData((prev) => ({
      ...prev,
      media: {
        ...prev.media,
        videos: prev.media.videos.filter((_, i) => i !== index),
      },
    }));

    toast.info("Video removed");
  };

  // Preview image URL
  const getImagePreview = (file) => {
    if (!file) return null;

    // If it's a File object (from preview), create object URL
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    // If it's already a string (uploaded path), return it
    if (typeof file === "string") {
      // If it's a relative path, prepend base URL if needed
      if (!file.startsWith("http") && !file.startsWith("/")) {
        return `${Baseurl}/${file}`;
      }
      return file;
    }

    return null;
  };

  // Get video preview URL
  const getVideoPreview = (file) => {
    if (!file) return null;

    // If it's a File object (from preview), create object URL
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    // If it's already a string (uploaded path), return it
    if (typeof file === "string") {
      // If it's a relative path, prepend base URL if needed
      if (!file.startsWith("http") && !file.startsWith("/")) {
        return `${Baseurl}/${file}`;
      }
      return file;
    }

    return null;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Form validation
  const validateForm = () => {
    // Basic info validation
    if (!formData.title || !formData.brand || !formData.model) {
      toast.error("Please fill in all required basic information");
      return false;
    }

    // Registration number validation
    if (!formData.registration.number) {
      toast.error("Please enter registration number");
      return false;
    }

    // Images validation - checking uploadedImagePaths
    if (uploadedImagePaths.length < 3) {
      toast.error("Please upload at least 3 images");
      return false;
    }

    return true;
  };

  // Format date for API
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return moment(date).utc().toISOString();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data
      const formattedData = {
        ...formData,
        registration: {
          ...formData.registration,
          date: formatDateForAPI(formData.registration.date),
          expiryDate: formatDateForAPI(formData.registration.expiryDate),
        },
        insurance: {
          ...formData.insurance,
          validUpto: formatDateForAPI(formData.insurance.validUpto),
        },
        permits: {
          ...formData.permits,
          validFrom: formatDateForAPI(formData.permits.validFrom),
          validUpto: formatDateForAPI(formData.permits.validUpto),
          nationalPermitUpto: formatDateForAPI(
            formData.permits.nationalPermitUpto,
          ),
        },
        puc: {
          ...formData.puc,
          validUpto: formatDateForAPI(formData.puc.validUpto),
        },
        documents: {
          ...formData.documents,
          batteryWarranty: {
            ...formData.documents.batteryWarranty,
            validTill: formatDateForAPI(
              formData.documents.batteryWarranty.validTill,
            ),
          },
          vehicleWarranty: {
            ...formData.documents.vehicleWarranty,
            validTill: formatDateForAPI(
              formData.documents.vehicleWarranty.validTill,
            ),
          },
        },
        valuationReport: formData?.valuationReport,
        vahanDetails: formData?.vahanDetails,
        auction: {
          ...formData.auction,
          startTime: formatDateForAPI(formData.auction.startTime),
          endTime: formatDateForAPI(formData.auction.endTime),
        },
        specifications: {
          ...formData.specifications,
        },
        media: formData.media,
      };

      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      
      const url = `${Baseurl}/api/vehicles/${id}`;
      const resp = await axios.put(url, formattedData, config);

      if (resp.data.success) {
        toast.success(resp.data.message);
        // Navigate back to vehicle list
        setTimeout(() => {
          navigate("/auctions/list");
        }, 1500);
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update vehicle"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (originalData) {
      const parsedData = parseDatesInVehicleData(originalData);
      setFormData(parsedData);
      toast.info("Form reset to original values");
    }
  };

  const handleCancel = () => {
    navigate("/auctions/list");
  };

  // Vahan Details Fetch Button
  const renderVahanButton = () => (
    <div className="mb-4 p-3 border rounded bg-light">
      <Row className="align-items-center">
        <Col md={8}>
          <Form.Group>
            <Form.Label>Registration Number *</Form.Label>
            <Form.Control
              type="text"
              name="registration.number"
              value={formData.registration.number}
              onChange={handleInputChange}
              placeholder="Enter registration number (e.g., MH12AB1234)"
              required
            />
            <Form.Text className="text-muted">
              Enter registration number to fetch Vahan details
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button
            variant="info"
            onClick={fetchVahanDetails}
            disabled={isFetchingVahan || !formData.registration.number}
            className="w-100"
          >
            {isFetchingVahan ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Fetching...
              </>
            ) : (
              "Fetch Vahan Details"
            )}
          </Button>
        </Col>
      </Row>
      {formData.vahanDetails && (
        <Alert variant="success" className="mt-3">
          <Alert.Heading>✓ Vahan Details Loaded</Alert.Heading>
          <p className="mb-0">
            Vehicle details have been auto-populated from Vahan database.
          </p>
        </Alert>
      )}
    </div>
  );

  // Render Media Tab
  const renderMediaTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Media Upload</h5>

      <Form.Group className="mb-4">
        <Form.Label>Images * (Minimum 3 required)</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleMultipleFileUpload(e, "images", true)}
        />
        <Form.Text className="text-muted">
          Upload additional images of the vehicle
        </Form.Text>

        {/* Show existing uploaded images */}
        {uploadedImagePaths.length > 0 && (
          <div className="mt-3">
            <h6>Current Images ({uploadedImagePaths.length} uploaded)</h6>
            <Row>
              {uploadedImagePaths.map((imagePath, index) => {
                const imageUrl = getImagePreview(imagePath);
                return (
                  <Col key={index} xs={6} md={4} lg={3} className="mb-3">
                    <Card>
                      <div style={{ height: "100px", overflow: "hidden" }}>
                        <img
                          src={imageUrl}
                          alt={`Image ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <Card.Body className="p-2">
                        <div className="small text-truncate mb-1">
                          Image {index + 1}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveImage(index)}
                          className="w-100"
                        >
                          Remove
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* Show new image previews */}
        {imageFiles.length > 0 && (
          <div className="mt-3">
            <h6>New Images to Upload ({imageFiles.length})</h6>
            <Row>
              {imageFiles.map((file, index) => {
                const imageUrl = getImagePreview(file);
                return (
                  <Col key={index} xs={6} md={4} lg={3} className="mb-3">
                    <Card>
                      <div style={{ height: "100px", overflow: "hidden" }}>
                        <img
                          src={imageUrl}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onLoad={() => {
                            if (file instanceof File) {
                              URL.revokeObjectURL(imageUrl);
                            }
                          }}
                        />
                      </div>
                      <Card.Body className="p-2">
                        <div className="small text-truncate mb-1">
                          {file.name || `Image ${index + 1}`}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveImage(index)}
                          className="w-100"
                        >
                          Remove
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {uploadedImagePaths.length < 3 && (
          <Alert variant="warning" className="mt-2">
            Please upload at least {3 - uploadedImagePaths.length} more image(s)
          </Alert>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Videos (Optional)</Form.Label>
        <Form.Control
          type="file"
          accept="video/*"
          multiple
          onChange={(e) => handleMultipleFileUpload(e, "videos", true)}
        />
        <Form.Text className="text-muted">
          Upload videos of the vehicle
        </Form.Text>

        {/* Show existing uploaded videos */}
        {uploadedVideoPaths.length > 0 && (
          <div className="mt-3">
            <h6>Current Videos ({uploadedVideoPaths.length})</h6>
            <Row>
              {uploadedVideoPaths.map((videoPath, index) => (
                <Col key={index} xs={12} md={6} className="mb-3">
                  <Card>
                    <div className="position-relative">
                      <div
                        style={{
                          height: "150px",
                          overflow: "hidden",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <video
                          src={getVideoPreview(videoPath)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          controls
                        />
                      </div>
                    </div>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong className="text-truncate d-block">
                            Video {index + 1}
                          </strong>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveVideo(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Form.Group>
    </div>
  );

  // Render Basic Info Tab
  const renderBasicInfoTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Basic Information</h5>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Title *</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Tata Nexon EV"
              required
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Brand *</Form.Label>
            <Form.Control
              type="text"
              name="brand"
              value={formData.brand}
              onChange={handleInputChange}
              placeholder="e.g., Tata, Tesla, MG"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Model *</Form.Label>
            <Form.Control
              type="text"
              name="model"
              value={formData.model}
              onChange={handleInputChange}
              placeholder="e.g., Nexon, Model 3, ZS EV"
              required
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Registration Number *</Form.Label>
            <Form.Control
              type="text"
              name="registration.number"
              value={formData.registration.number}
              onChange={handleInputChange}
              placeholder="Enter registration number (e.g., MH12AB1234)"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Color</Form.Label>
            <Form.Control
              type="text"
              name="color"
              value={formData.color}
              onChange={handleInputChange}
              placeholder="e.g., Red, Blue, White"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Manufacturing Year *</Form.Label>
            <Form.Control
              type="number"
              name="manufacturing.year"
              value={formData.manufacturing.year}
              onChange={handleInputChange}
              min={1900}
              max={new Date().getFullYear()}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Chassis Number</Form.Label>
            <Form.Control
              type="text"
              name="identifiers.chassis"
              value={formData.identifiers.chassis}
              onChange={handleInputChange}
              placeholder="Chassis/VIN number"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Engine Number</Form.Label>
            <Form.Control
              type="text"
              name="identifiers.engine"
              value={formData.identifiers.engine}
              onChange={handleInputChange}
              placeholder="Engine number"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Valuation Report ** (Paste the url only)</Form.Label>
            <Form.Control
              type="text"
              name="valuationReport"
              value={formData.valuationReport}
              onChange={handleInputChange}
              placeholder="Valuation Report"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Variant</Form.Label>
            <Form.Control
              type="text"
              name="variant"
              value={formData.variant}
              onChange={handleInputChange}
              placeholder="e.g., XZ+ Lux, Performance"
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Vehicle Category</Form.Label>
            <Form.Select
              name="specifications.category"
              value={formData?.specifications?.category}
              onChange={handleInputChange}
            >
              <option value="Car">Car</option>
              <option value="SUV">SUV</option>
              <option value="Two Wheeler">Two Wheeler</option>
              <option value="Three Wheeler">Three Wheeler</option>
              <option value="Commercial">Commercial</option>
              <option value="Other">Other</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  // Render EV Specs Tab
  const renderEVSpecsTab = () => (
    <div className="p-3">
      <h5 className="mb-4">EV Specifications</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Battery Capacity *</Form.Label>
            <Row>
              <Col>
                <Form.Control
                  type="number"
                  name="evSpecs.batteryCapacity.value"
                  value={formData.evSpecs.batteryCapacity.value}
                  onChange={handleInputChange}
                  placeholder="e.g., 40"
                  required
                />
              </Col>
              <Col>
                <Form.Select
                  name="evSpecs.batteryCapacity.unit"
                  value={formData.evSpecs.batteryCapacity.unit}
                  onChange={handleInputChange}
                >
                  <option value="kWh">kWh</option>
                  <option value="Ah">Ah</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Range *</Form.Label>
            <Row>
              <Col>
                <Form.Control
                  type="number"
                  name="evSpecs.range.value"
                  value={formData.evSpecs.range.value}
                  onChange={handleInputChange}
                  placeholder="e.g., 300"
                  required
                />
              </Col>
              <Col>
                <Form.Select
                  name="evSpecs.range.unit"
                  value={formData.evSpecs.range.unit}
                  onChange={handleInputChange}
                >
                  <option value="km">km</option>
                  <option value="miles">miles</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Battery Type</Form.Label>
            <Form.Select
              name="evSpecs.batteryType"
              value={formData.evSpecs.batteryType}
              onChange={handleInputChange}
            >
              <option value="Li-ion">Li-ion</option>
              <option value="Li-Po">Li-Po</option>
              <option value="Lead Acid">Lead Acid</option>
              <option value="Solid State">Solid State</option>
              <option value="Other">Other</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Battery Health (%)</Form.Label>
            <Form.Control
              type="number"
              name="evSpecs.batteryHealthPercent"
              value={formData.evSpecs.batteryHealthPercent}
              onChange={handleInputChange}
              min="0"
              max="100"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Charging Type</Form.Label>
            <Form.Select
              name="evSpecs.charging.type"
              value={formData.evSpecs.charging.type}
              onChange={handleInputChange}
            >
              <option value="AC">AC</option>
              <option value="DC">DC</option>
              <option value="Fast">Fast</option>
              <option value="Swappable">Swappable</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Charger Included"
              checked={formData.evSpecs.chargerStatus}
              onChange={(e) =>
                handleNestedChange("evSpecs", "chargerStatus", e.target.checked)
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Normal Charging Time (hours)</Form.Label>
            <Form.Control
              type="number"
              name="evSpecs.charging.normalTime"
              value={formData.evSpecs.charging.normalTime}
              onChange={handleInputChange}
              placeholder="e.g., 8"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Fast Charging Time (minutes)</Form.Label>
            <Form.Control
              type="number"
              name="evSpecs.charging.fastTime"
              value={formData.evSpecs.charging.fastTime}
              onChange={handleInputChange}
              placeholder="e.g., 45"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Motor Power</Form.Label>
            <Row>
              <Col>
                <Form.Control
                  type="number"
                  name="evSpecs.motorPower.value"
                  value={formData.evSpecs.motorPower.value}
                  onChange={handleInputChange}
                  placeholder="e.g., 150"
                />
              </Col>
              <Col>
                <Form.Select
                  name="evSpecs.motorPower.unit"
                  value={formData.evSpecs.motorPower.unit}
                  onChange={handleInputChange}
                >
                  <option value="kW">kW</option>
                  <option value="HP">HP</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Max Speed (km/h)</Form.Label>
            <Form.Control
              type="number"
              name="evSpecs.maxSpeed"
              value={formData.evSpecs.maxSpeed}
              onChange={handleInputChange}
              placeholder="e.g., 180"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  // Render Yard Details Tab
  const renderYardTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Yard/Location Details</h5>

      <Form.Group className="mb-3">
        <Form.Label>Yard/Parked At *</Form.Label>
        <Form.Control
          type="text"
          name="yard.parkedAt"
          value={formData.yard.parkedAt}
          onChange={handleInputChange}
          placeholder="e.g., Mumbai Yard, Delhi Warehouse"
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Full Address *</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="yard.address"
          value={formData.yard.address}
          onChange={handleInputChange}
          placeholder="Enter complete yard address"
          required
        />
      </Form.Group>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Point of Contact Name *</Form.Label>
            <Form.Control
              type="text"
              name="yard.pocName"
              value={formData.yard.pocName}
              onChange={handleInputChange}
              placeholder="e.g., John Doe"
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>POC Contact Number *</Form.Label>
            <Form.Control
              type="text"
              name="yard.pocContact"
              value={formData.yard.pocContact}
              onChange={handleInputChange}
              placeholder="e.g., 9876543210"
              required
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderInsuranceTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Insurance Details</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Company name *</Form.Label>
            <Form.Control
              type="text"
              name="insurance.company"
              value={formData.insurance.company}
              onChange={handleInputChange}
              placeholder="e.g., TATA AIG GENERAL INSURANCE CO. LTD."
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Policy Number *</Form.Label>
            <Form.Control
              type="text"
              name="insurance.policyNumber"
              value={formData.insurance.policyNumber}
              onChange={handleInputChange}
              placeholder="e.g., 37633047040000"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Valid Upto *</Form.Label>
            <DatePicker
              selected={formData.insurance.validUpto}
              onChange={(date) =>
                handleNestedChange("insurance", "validUpto", date)
              }
              className="form-control"
              dateFormat="dd/MM/yyyy"
              placeholderText="Select date"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  // Render Registration Tab
  const renderRegistrationTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Registration & Ownership</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Registration Date</Form.Label>
            <DatePicker
              selected={formData.registration.date}
              onChange={(date) =>
                handleNestedChange("registration", "date", date)
              }
              className="form-control"
              dateFormat="dd/MM/yyyy"
              placeholderText="Select date"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Registration Expiry</Form.Label>
            <DatePicker
              selected={formData.registration.expiryDate}
              onChange={(date) =>
                handleNestedChange("registration", "expiryDate", date)
              }
              className="form-control"
              dateFormat="dd/MM/yyyy"
              placeholderText="Select date"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>RTO Authority</Form.Label>
            <Form.Control
              type="text"
              name="registration.authority"
              value={formData.registration.authority}
              onChange={handleInputChange}
              placeholder="e.g., Mumbai RTO"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>RTO Code</Form.Label>
            <Form.Control
              type="text"
              name="registration.rtoCode"
              value={formData.registration.rtoCode}
              onChange={handleInputChange}
              placeholder="e.g., MH01"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Owner Name</Form.Label>
            <Form.Control
              type="text"
              name="owner.name"
              value={formData.owner.name}
              onChange={handleInputChange}
              placeholder="Owner's full name"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Owner Mobile</Form.Label>
            <Form.Control
              type="text"
              name="owner.mobile"
              value={formData.owner.mobile}
              onChange={handleInputChange}
              placeholder="Mobile number"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Owner Count</Form.Label>
            <Form.Select
              name="owner.count"
              value={formData.owner.count}
              onChange={handleInputChange}
            >
              <option value="First">First</option>
              <option value="Second">Second</option>
              <option value="Third">Third</option>
              <option value="Fourth+">Fourth+</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Commercial Vehicle"
              checked={formData.registration.isCommercial}
              onChange={(e) =>
                handleNestedChange("registration", "isCommercial", e.target.checked)
              }
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  // Render Documents Tab
  const renderDocumentsTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Vehicle Documents</h5>

      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0">RC Book & Insurance</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>RC Book Copy</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleMultipleFileUpload(e, "documents.rcBook", false)
                  }
                />
                {formData.documents.rcBook && (
                  <Form.Text className="text-success">
                    ✓ {formData.documents.rcBook}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Insurance Copy</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleMultipleFileUpload(
                      e,
                      "documents.insuranceCopy",
                      false,
                    )
                  }
                />
                {formData.documents.insuranceCopy && (
                  <Form.Text className="text-success">
                    ✓ {formData.documents.insuranceCopy}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0">Other Documents</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fitness Certificate</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleMultipleFileUpload(
                      e,
                      "documents.fitnessCertificate",
                      false,
                    )
                  }
                />
                {formData.documents.fitnessCertificate && (
                  <Form.Text className="text-success">
                    ✓ {formData.documents.fitnessCertificate}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tax Receipt</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleMultipleFileUpload(e, "documents.taxReceipt", false)
                  }
                />
                {formData.documents.taxReceipt && (
                  <Form.Text className="text-success">
                    ✓ {formData.documents.taxReceipt}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Invoice/Bill</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    handleMultipleFileUpload(e, "documents.invoice", false)
                  }
                />
                {formData.documents.invoice && (
                  <Form.Text className="text-success">
                    ✓ {formData.documents.invoice}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );

  // Render Auction Tab
  const renderAuctionTab = () => (
    <div className="p-3">
      <h5 className="mb-4">Auction Details</h5>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Expected Price (₹)</Form.Label>
            <Form.Control
              type="text"
              name="pricing.expectedPrice"
              value={formData.pricing.expectedPrice}
              onChange={handleInputChange}
              placeholder="e.g., 8-10 Lakhs"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Base Price (₹) *</Form.Label>
            <Form.Control
              type="number"
              name="pricing.basePrice"
              value={formData.pricing.basePrice}
              onChange={handleInputChange}
              min="0"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Reserve Price (₹)</Form.Label>
            <Form.Control
              type="number"
              name="pricing.reservePrice"
              value={formData.pricing.reservePrice}
              onChange={handleInputChange}
              min="0"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Auction Status</Form.Label>
            <Form.Select
              name="auction.status"
              value={formData.auction.status}
              onChange={handleInputChange}
            >
              <option value="Draft">Draft</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Live">Live</option>
              <option value="Ended">Ended</option>
              <option value="Sold">Sold</option>
              <option value="Unsold">Unsold</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Auction Start Time</Form.Label>
            <DatePicker
              selected={formData.auction.startTime}
              onChange={(date) =>
                handleNestedChange("auction", "startTime", date)
              }
              className="form-control"
              showTimeSelect
              dateFormat="dd/MM/yyyy hh:mm aa"
              timeFormat="HH:mm"
              placeholderText="Select date and time"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Auction End Time</Form.Label>
            <DatePicker
              selected={formData.auction.endTime}
              onChange={(date) =>
                handleNestedChange("auction", "endTime", date)
              }
              className="form-control"
              showTimeSelect
              dateFormat="dd/MM/yyyy hh:mm aa"
              timeFormat="HH:mm"
              placeholderText="Select date and time"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const getProgress = () => {
    const tabs = [
      "basic",
      "ev",
      "insurance",
      "yard",
      "registration",
      "media",
      "documents",
      "auction",
    ];
    const currentIndex = tabs.indexOf(activeTab);
    return ((currentIndex + 1) / tabs.length) * 100;
  };

  if (isLoading) {
    return (
      <Container fluid className="py-4">
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading vehicle data...</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-0">Edit Vehicle</h3>
              <p className="text-muted mb-0">
                Update vehicle details. All required fields are marked with *
              </p>
              <small className="text-muted">Vehicle ID: {id}</small>
            </div>
            <div>
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-2"
              >
                Reset Changes
              </Button>
              <Button variant="outline-danger" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Vahan Details Fetch Section */}
          {renderVahanButton()}

          <ProgressBar
            now={getProgress()}
            className="mb-4"
            label={`${Math.round(getProgress())}%`}
            visuallyHidden
          />

          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Nav
              variant="tabs"
              className="mb-4 flex-nowrap"
              style={{ overflowX: "auto" }}
            >
              <Nav.Item>
                <Nav.Link eventKey="basic">Basic Info</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="ev">EV Specs</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="insurance">Insurance Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="yard">Yard Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="registration">Registration</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="media">Media</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">Documents</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="auction">Auction</Nav.Link>
              </Nav.Item>
            </Nav>

            <Form onSubmit={handleSubmit}>
              <Tab.Content>
                <Tab.Pane eventKey="basic">{renderBasicInfoTab()}</Tab.Pane>
                <Tab.Pane eventKey="ev">{renderEVSpecsTab()}</Tab.Pane>
                <Tab.Pane eventKey="insurance">{renderInsuranceTab()}</Tab.Pane>
                <Tab.Pane eventKey="yard">{renderYardTab()}</Tab.Pane>
                <Tab.Pane eventKey="registration">
                  {renderRegistrationTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="media">{renderMediaTab()}</Tab.Pane>
                <Tab.Pane eventKey="documents">{renderDocumentsTab()}</Tab.Pane>
                <Tab.Pane eventKey="auction">{renderAuctionTab()}</Tab.Pane>
              </Tab.Content>

              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    const tabs = [
                      "basic",
                      "ev",
                      "insurance",
                      "yard",
                      "registration",
                      "media",
                      "documents",
                      "auction",
                    ];
                    const currentIndex = tabs.indexOf(activeTab);
                    if (currentIndex > 0) {
                      setActiveTab(tabs[currentIndex - 1]);
                    }
                  }}
                  disabled={activeTab === "basic"}
                >
                  Previous
                </Button>

                <div>
                  <Button
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => {
                      const tabs = [
                        "basic",
                        "ev",
                        "insurance",
                        "yard",
                        "registration",
                        "media",
                        "documents",
                        "auction",
                      ];
                      const currentIndex = tabs.indexOf(activeTab);
                      if (currentIndex < tabs.length - 1) {
                        setActiveTab(tabs[currentIndex + 1]);
                      }
                    }}
                    disabled={activeTab === "auction"}
                  >
                    Next
                  </Button>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Vehicle"}
                  </Button>
                </div>
              </div>
            </Form>
          </Tab.Container>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditVehicle;